/**
 * PeoplePanel - the Connected Users panel (top-left region): roster with
 * Nearby/All tabs, per-row media/network/level glyphs, the moderation kebab
 * menu, live role promotion, and the moderators' "Waiting to join" lobby
 * queue. React replacement for multiplayerUI's renderPanel/renderRow/
 * refreshTick machinery - reconciliation replaces the membership-signature
 * diff, the in-place distance-hint writes, and the "defer rebuild while a
 * menu is open" hack (the open menu is state, so re-renders never close it).
 *
 * E2E contracts preserved exactly (multiplayerVideo/voiceSpatial specs):
 * - #photon-user-list container id
 * - .roster-tab[data-tab="nearby"|"all"] with text 'Nearby (n)' / 'All (n)'
 * - each row is a div with exactly two <span> children; the SECOND span
 *   holds the media glyphs whose title attrs are exactly
 *   'mic on' | 'mic off' | 'camera on' | 'camera off'
 * - a live mic's span carries data-speaking-uid=<sanitized uid> (muted rows
 *   never do, so mute suppression stays structural); the speaking tint is
 *   the same #22c55e color write
 *
 * Data: roomStore.roster (event-driven pushes from the updateUserList
 * adapter + the 1 Hz publisher for live distances), sessionStore.userRole
 * for gating, roomStore.lobby for the queue, roomStore.speaking for tints.
 * Handlers ride the registry; Remove keeps its modalManager confirm.
 */
import { useMemo, useRef, useState } from 'react';
import { useStore } from 'zustand';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Circle,
  Ellipsis,
  ScreenShareOff,
  UserX,
  UserCheck,
  UserPlus,
  UserMinus,
  ChevronUp,
  ChevronDown,
} from 'lucide';
import { can, roleLabel } from '../../config/roles.js';
import { TOKENS } from '../designSystem.js';
import { createNearbyTracker } from '../../services/nearby.js';
import { modalManager } from '../ModalManager.js';
import { roomStore } from '../../state/roomStore';
import type { RosterEntry, LobbyEntry } from '../../state/roomStore';
import { sessionStore } from '../../state/sessionStore';
import { hudStore } from '../../state/hudStore';
import { getPlayerColor } from '../../services/playerColor.js';
import { showNotice } from '../../state/hudStore';
import { getHandlers } from './handlerRegistry';
import { networkQualityColor } from './networkDotShared';
import { Icon } from './Icon';
import type { LucideIconNode } from '../../utils/iconCanvas';

const ic = (i: unknown) => i as LucideIconNode;

function sanitizeUid(uid: unknown): string {
  return String(uid ?? '').replace(/[^\w-]/g, '');
}

/** Mic/camera glyphs; a live mic carries the data-speaking-uid hook. */
function MediaIcons({ entry, speaking }: { entry: RosterEntry; speaking: ReadonlySet<string> }) {
  const mediaState = entry.mediaState;
  if (!mediaState) {
    return (
      <span
        title="media state unknown"
        style={{
          opacity: 0.35,
          marginLeft: '6px',
          display: 'inline-flex',
          gap: '3px',
          verticalAlign: 'middle',
        }}
      >
        <Icon icon={ic(Mic)} size={14} />
        <Icon icon={ic(Video)} size={14} />
      </span>
    );
  }
  const uid = entry.agoraUid != null ? sanitizeUid(entry.agoraUid) : null;
  const isSpeaking = uid != null && speaking.has(uid);
  return (
    <span
      style={{ marginLeft: '6px', display: 'inline-flex', gap: '3px', verticalAlign: 'middle' }}
    >
      {mediaState.mic ? (
        <span
          title="mic on"
          {...(uid != null ? { 'data-speaking-uid': uid } : {})}
          style={{ display: 'inline-flex', color: isSpeaking ? '#22c55e' : undefined }}
        >
          <Icon icon={ic(Mic)} size={14} />
        </span>
      ) : (
        <span title="mic off" style={{ display: 'inline-flex', opacity: 0.5 }}>
          <Icon icon={ic(MicOff)} size={14} />
        </span>
      )}
      {mediaState.camera ? (
        <span title="camera on" style={{ display: 'inline-flex' }}>
          <Icon icon={ic(Video)} size={14} />
        </span>
      ) : (
        <span title="camera off" style={{ display: 'inline-flex', opacity: 0.5 }}>
          <Icon icon={ic(VideoOff)} size={14} />
        </span>
      )}
    </span>
  );
}

const menuItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  padding: '4px 10px',
  background: 'none',
  border: 'none',
  color: '#fff',
  fontSize: '12px',
  cursor: 'pointer',
  textAlign: 'left',
};

function ModMenu({
  entry,
  open,
  onToggle,
  onClose,
}: {
  entry: RosterEntry;
  open: boolean;
  onToggle(): void;
  onClose(): void;
}) {
  const name = entry.name;
  const runModeration = async (action: string) => {
    const moderation = getHandlers().moderation;
    if (!moderation) return;
    try {
      await moderation.onAction(action, entry.actorNr, name);
      showNotice(
        action === 'mute'
          ? `Muted ${name}`
          : action === 'camera-off'
            ? `Turned off ${name}'s camera`
            : action === 'stop-share'
              ? `Stopped ${name}'s screen share`
              : `Removed ${name}`,
      );
    } catch (err) {
      showNotice(`Moderation failed: ${(err as Error).message}`, true);
    } finally {
      onClose();
    }
  };
  const setRole = async (role: string) => {
    const roleHandlers = getHandlers().role;
    if (!roleHandlers) return;
    try {
      await roleHandlers.onSetRole(entry.actorNr, name, role);
      showNotice(role === 'presenter' ? `${name} is now a presenter` : `${name} is now a guest`);
    } catch (err) {
      const status = (err as { status?: number }).status;
      showNotice(
        status === 409
          ? 'Another host just changed that role'
          : `Could not change role: ${(err as Error).message}`,
        true,
      );
    } finally {
      onClose();
    }
  };

  return (
    <span style={{ position: 'relative', marginLeft: '6px' }}>
      <button
        type="button"
        className="mod-menu-btn"
        data-actor={entry.actorNr}
        title="Moderation"
        onClick={onToggle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          color: '#fff',
          cursor: 'pointer',
          lineHeight: 1,
          padding: '0 2px',
        }}
      >
        <Icon icon={ic(Ellipsis)} size={16} />
      </button>
      {open && (
        <span
          className="mod-menu"
          data-actor={entry.actorNr}
          style={{
            display: 'block',
            position: 'absolute',
            top: '18px',
            right: 0,
            background: '#222',
            border: '1px solid #444',
            borderRadius: '6px',
            padding: '4px 0',
            minWidth: '120px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
            zIndex: 103,
            textAlign: 'left',
          }}
        >
          <button
            className="mod-action"
            style={menuItemStyle}
            onClick={() => runModeration('mute')}
          >
            <Icon icon={ic(MicOff)} size={14} /> Mute
          </button>
          <button
            className="mod-action"
            style={menuItemStyle}
            onClick={() => runModeration('camera-off')}
          >
            <Icon icon={ic(VideoOff)} size={14} /> Camera off
          </button>
          <button
            className="mod-action"
            style={menuItemStyle}
            onClick={() => runModeration('stop-share')}
          >
            <Icon icon={ic(ScreenShareOff)} size={14} /> Stop share
          </button>
          {entry.role === 'guest' && (
            <button
              className="role-action"
              style={menuItemStyle}
              onClick={() => setRole('presenter')}
            >
              <Icon icon={ic(UserPlus)} size={14} /> Make presenter
            </button>
          )}
          {entry.role === 'presenter' && (
            <button className="role-action" style={menuItemStyle} onClick={() => setRole('guest')}>
              <Icon icon={ic(UserMinus)} size={14} /> Make guest
            </button>
          )}
          <button
            className="mod-action"
            style={{ ...menuItemStyle, color: '#ff7a76' }}
            onClick={() => {
              // In-app confirm (dark theme) instead of the jarring window.confirm.
              modalManager.confirm({
                title: 'Remove from session',
                message: `Remove ${name}? They will be disconnected.`,
                confirmText: 'Remove',
                confirmType: 'danger',
                theme: 'dark',
                onConfirm: () => {
                  runModeration('remove');
                },
              });
            }}
          >
            <Icon icon={ic(UserX)} size={14} /> Remove
          </button>
        </span>
      )}
    </span>
  );
}

/**
 * One participant row. STRUCTURE CONTRACT: a div with exactly two span
 * children - left (dot + name + role badge), right (distance/media/network/
 * level/moderation) - the multiplayerVideo spec addresses the media glyphs
 * as span[2] of the row.
 */
function Row({
  entry,
  currentLevel,
  iCanModerate,
  showDistance,
  speaking,
  menuOpenFor,
  setMenuOpenFor,
}: {
  entry: RosterEntry;
  currentLevel: string | null;
  iCanModerate: boolean;
  showDistance: boolean;
  speaking: ReadonlySet<string>;
  menuOpenFor: number | null;
  setMenuOpenFor(actorNr: number | null): void;
}) {
  const name = entry.name || `User ${entry.actorNr}`;
  const userLevel = entry.level || 'Unknown';
  const isInSameLevel = userLevel === currentLevel;
  const roleText = roleLabel(entry.role);
  const net = networkQualityColor(entry.networkQuality);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        lineHeight: 1.7,
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            flex: 'none',
            width: '12px',
            height: '12px',
            background: entry.color || '#cccccc',
            borderRadius: '50%',
            marginRight: '8px',
          }}
        />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
        {roleText ? (
          <span
            style={{
              fontSize: '11px',
              background: 'rgba(255,255,255,0.18)',
              borderRadius: '6px',
              padding: '1px 6px',
              marginLeft: '4px',
            }}
          >
            {roleText}
          </span>
        ) : null}
      </span>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          flex: 'none',
          whiteSpace: 'nowrap',
          marginLeft: '12px',
        }}
      >
        {showDistance && typeof entry.distance === 'number' ? (
          <span
            className="dist-hint"
            data-actor={entry.actorNr}
            style={{ color: '#9ca3af', fontSize: '11px', verticalAlign: 'middle' }}
          >
            {Math.round(entry.distance)}m
          </span>
        ) : null}
        <MediaIcons entry={entry} speaking={speaking} />
        <span
          title={`connection: ${net.label}`}
          style={{
            display: 'inline-block',
            width: '9px',
            height: '9px',
            borderRadius: '50%',
            background: net.color,
            marginLeft: '6px',
            verticalAlign: 'middle',
          }}
        />{' '}
        <span
          style={{
            color: isInSameLevel ? '#00ff00' : '#ffaa00',
            display: 'inline-flex',
            alignItems: 'center',
            margin: '0 2px',
            verticalAlign: 'middle',
          }}
        >
          <Icon icon={ic(Circle)} size={10} fill={isInSameLevel ? 'currentColor' : 'none'} />
        </span>
        {iCanModerate && entry.sameLevel && !entry.isSelf ? (
          <ModMenu
            entry={entry}
            open={menuOpenFor === entry.actorNr}
            onToggle={() => setMenuOpenFor(menuOpenFor === entry.actorNr ? null : entry.actorNr)}
            onClose={() => setMenuOpenFor(null)}
          />
        ) : null}
      </span>
    </div>
  );
}

function LobbySection({ pending }: { pending: LobbyEntry[] }) {
  const [busy, setBusy] = useState<string | null>(null);
  if (!pending.length) return <div id="lobby-section" />;

  const decide = async (action: 'admit' | 'deny', entry: LobbyEntry) => {
    const lobby = getHandlers().lobby;
    if (!lobby) return;
    const name = entry.displayName || 'Guest';
    setBusy(entry.id);
    try {
      await lobby.onDecision(action, entry.id, name);
      showNotice(action === 'admit' ? `Let ${name} in` : `Denied ${name}`);
    } catch (err) {
      // A 409 just means another host already handled it - the row drops on
      // the next poll either way.
      const status = (err as { status?: number }).status;
      showNotice(
        status === 409
          ? 'Already handled by another host'
          : `${action === 'admit' ? 'Admit' : 'Deny'} failed: ${(err as Error).message}`,
        status !== 409,
      );
    } finally {
      setBusy(null);
    }
  };

  const btnStyle: React.CSSProperties = {
    border: 'none',
    borderRadius: '6px',
    color: '#fff',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    padding: '2px 8px',
  };

  return (
    <div id="lobby-section">
      <div
        style={{
          fontSize: '12px',
          color: '#e5e7eb',
          fontWeight: 'bold',
          margin: '2px 0 4px',
        }}
      >
        Waiting to join ({pending.length})
      </div>
      {pending.map((p) => {
        const name = p.displayName || 'Guest';
        return (
          <div
            key={p.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              lineHeight: 1.9,
            }}
          >
            <span
              style={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </span>
            <span style={{ display: 'flex', flex: 'none', gap: '6px' }}>
              <button
                className="lobby-action"
                data-action="admit"
                data-id={p.id}
                title={`Let ${name} in`}
                disabled={busy === p.id}
                onClick={() => decide('admit', p)}
                style={{ ...btnStyle, background: 'rgba(0,150,90,0.85)' }}
              >
                <Icon icon={ic(UserCheck)} size={14} /> Admit
              </button>
              <button
                className="lobby-action"
                data-action="deny"
                data-id={p.id}
                title={`Don't let ${name} in`}
                disabled={busy === p.id}
                onClick={() => decide('deny', p)}
                style={{ ...btnStyle, background: 'rgba(216,49,47,0.85)' }}
              >
                <Icon icon={ic(UserX)} size={14} /> Deny
              </button>
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function PeoplePanel() {
  const roster = useStore(roomStore, (s) => s.roster);
  const speaking = useStore(roomStore, (s) => s.speaking.uids);
  const lobby = useStore(roomStore, (s) => s.lobby);
  const userRole = useStore(sessionStore, (s) => s.userRole);
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<'nearby' | 'all'>('nearby');
  const [menuOpenFor, setMenuOpenFor] = useState<number | null>(null);

  // Same hysteresis model as before: the tracker keeps its enter/exit band
  // across renders, fed by each roster push.
  const trackerRef = useRef<ReturnType<typeof createNearbyTracker> | null>(null);
  if (!trackerRef.current) trackerRef.current = createNearbyTracker();
  const nearbyEntries = useMemo(() => {
    // The tracker filters/sorts the entry objects it is fed and returns the
    // same objects; its own typedef predates the extended row fields (and
    // types distance as non-null), so one identity-preserving cast is honest.
    const update = trackerRef.current!.update as unknown as (e: RosterEntry[]) => RosterEntry[];
    return update(roster.entries.filter((e) => e.sameLevel));
  }, [roster]);

  const playerName = useStore(sessionStore, (s) => s.playerName);
  const selfMedia = useStore(hudStore, (s) => s.mediaState);

  // The All tab includes the local player (the imperative panel counted the
  // avatar map, which multiplayer.js seeds with a self record on join). The
  // roster snapshot is remotes-only, so the row is synthesized from the
  // identity + media stores; Nearby stays remotes-only by design.
  const allEntries = useMemo(() => {
    const self = {
      actorNr: -1,
      isSelf: true,
      name: playerName || 'You',
      role: userRole || undefined,
      sameLevel: true,
      level: roster.currentLevel,
      breakoutRoomId: null,
      distance: null,
      color: getPlayerColor() || undefined,
      mediaState: { mic: selfMedia.mic, camera: selfMedia.camera },
    } as RosterEntry;
    return [self, ...roster.entries].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || '')),
    );
  }, [roster, playerName, userRole, selfMedia]);

  const iCanModerate = can(userRole, 'moderate');
  const currentLevel = roster.currentLevel;

  const tabStyle = (active: boolean): React.CSSProperties => ({
    border: 'none',
    borderRadius: `${TOKENS.radius.pill}px`,
    padding: '2px 10px',
    fontSize: '12px',
    fontFamily: 'inherit',
    color: '#fff',
    cursor: 'pointer',
    background: active ? TOKENS.action.primary.background : 'rgba(255,255,255,0.08)',
  });

  const emptyState = (text: string) => (
    <div style={{ color: '#9ca3af', fontSize: '12px', padding: '4px 2px' }}>{text}</div>
  );

  const rows =
    activeTab === 'nearby'
      ? nearbyEntries.length
        ? nearbyEntries.map((e) => (
            <Row
              key={e.actorNr}
              entry={e}
              currentLevel={currentLevel}
              iCanModerate={iCanModerate}
              showDistance
              speaking={speaking}
              menuOpenFor={menuOpenFor}
              setMenuOpenFor={setMenuOpenFor}
            />
          ))
        : emptyState('No one within earshot. Walk up to someone to talk.')
      : allEntries.length
        ? allEntries.map((e) => (
            <Row
              key={e.actorNr}
              entry={e}
              currentLevel={currentLevel}
              iCanModerate={iCanModerate}
              showDistance={false}
              speaking={speaking}
              menuOpenFor={menuOpenFor}
              setMenuOpenFor={setMenuOpenFor}
            />
          ))
        : emptyState('No one else is connected.');

  return (
    <div
      id="photon-user-list"
      style={{
        background: 'rgba(18,18,22,0.85)',
        color: '#fff',
        padding: '12px 16px',
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.12)',
        backdropFilter: 'blur(4px)',
        fontFamily: 'sans-serif',
        fontSize: '15px',
        minWidth: '150px',
        maxWidth: '360px',
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontWeight: 'bold',
          marginBottom: '6px',
        }}
      >
        <span>People</span>
        <span style={{ display: 'flex', flex: 'none' }}>
          <button
            id="user-list-collapse"
            title={collapsed ? 'Show participants' : 'Hide participants'}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0 2px',
              marginLeft: '6px',
            }}
          >
            <Icon icon={ic(collapsed ? ChevronDown : ChevronUp)} size={14} />
          </button>
        </span>
      </div>
      {iCanModerate ? <LobbySection pending={lobby.pending} /> : null}
      <div id="user-list-rows" style={{ display: collapsed ? 'none' : 'block' }}>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
          <button
            className="roster-tab"
            data-tab="nearby"
            title="People within voice range"
            onClick={() => setActiveTab('nearby')}
            style={tabStyle(activeTab === 'nearby')}
          >
            Nearby ({nearbyEntries.length})
          </button>
          <button
            className="roster-tab"
            data-tab="all"
            title="Everyone in the session"
            onClick={() => setActiveTab('all')}
            style={tabStyle(activeTab === 'all')}
          >
            All ({allEntries.length})
          </button>
        </div>
        {rows}
      </div>
    </div>
  );
}
